import {
  AnimatePresence,
  motion,
  type HTMLMotionProps,
  type Target,
  type TargetAndTransition,
} from "motion/react";
import { forwardRef, useMemo } from "react";

import { cn } from "../../lib/cn";

export type TransitionAnimation = {
  initial?: Target;
  animate?: TargetAndTransition;
  exit?: TargetAndTransition;
};

export type TransitionAnimations = {
  in?: TransitionAnimation;
  out?: TransitionAnimation;
};

export type TransitionMode = "animate" | "hidden" | "static";

type TransitionProps = Omit<HTMLMotionProps<"div">, "children"> & {
  children?: React.ReactNode;
  mode?: TransitionMode;
  duration?: number;
  animations?: TransitionAnimations;
  useVerticalScroll?: boolean;
  animateOnInitialMount?: boolean;
};

function buildEnterAnimation(duration: number): TransitionAnimation {
  return {
    initial: {
      opacity: 0,
      y: 8,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: duration / 1000,
        ease: "easeOut",
      },
    },
  };
}

function buildExitAnimation(duration: number): TransitionAnimation {
  return {
    exit: {
      opacity: 0,
      y: 8,
      transition: {
        duration: duration / 1000,
        ease: "easeOut",
      },
    },
  };
}

export const AnimateTransition = forwardRef<HTMLDivElement, TransitionProps>(
  (
    {
      mode = "animate",
      duration = 200,
      animations,
      useVerticalScroll = false,
      animateOnInitialMount = false,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const enterAnimation = useMemo(
      () => animations?.in ?? buildEnterAnimation(duration),
      [animations?.in, duration],
    );
    const exitAnimation = useMemo(
      () => animations?.out ?? buildExitAnimation(duration),
      [animations?.out, duration],
    );

    const wrapperClassName = cn(
      useVerticalScroll && "flex flex-1 flex-col overflow-auto",
      className,
    );

    if (mode === "hidden") {
      return null;
    }

    if (mode === "static") {
      return (
        <div
          ref={ref}
          className={wrapperClassName}
          {...(props as React.HTMLAttributes<HTMLDivElement>)}
        >
          {children}
        </div>
      );
    }

    return (
      <AnimatePresence initial={animateOnInitialMount} mode="wait">
        <motion.div
          ref={ref}
          className={wrapperClassName}
          initial={enterAnimation.initial}
          animate={enterAnimation.animate}
          exit={exitAnimation.exit}
          {...props}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    );
  },
);

AnimateTransition.displayName = "AnimateTransition";
